import { useEffect, useRef } from 'react'
import { Box, CssBaseline, ThemeProvider, createTheme } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import Beacon4FilterBar              from './components/filters/Beacon4FilterBar'
import Beacon4LegendPanel            from './components/filters/Beacon4LegendPanel'
import Beacon4PerformanceLegendPanel from './components/filters/Beacon4PerformanceLegendPanel'
import Beacon4BlendedView            from './components/Beacon4BlendedView'
import Beacon4PerformanceView        from './components/Beacon4PerformanceView'
import Beacon4ExportButton           from './components/Beacon4ExportButton'
import { selectBeacon4Module }                  from './reducers/Beacon4FiltersReducer'
import { loadBeacon4PerformanceAvailableKpis } from './reducers/Beacon4PerformanceKpiDataReducer'

const theme = createTheme({
  typography: { fontFamily: "'IBM Plex Sans', sans-serif" },
  palette:    { primary: { main: '#1565c0' }, background: { default: '#f0f2f5' } },
})

export default function App({ externalRole }) {
  const chartAreaRef = useRef(null)
  const dispatch     = useDispatch()
  const module       = useSelector(selectBeacon4Module)

  // Load the techs + main-KPIs lookup whenever the Performance module is active.
  // The backend caches this for 5 min, so toggling back and forth is cheap.
  useEffect(() => {
    if (module === 'Performance') dispatch(loadBeacon4PerformanceAvailableKpis())
  }, [dispatch, module])

  // The whole app is a flex column that fills the height the host shell gives
  // us. We deliberately avoid `calc(100vh - …)` because the Beacon4 app is
  // mounted inside the Cyient shell (topbar + sidebar) — 100vh is bigger than
  // the area where we actually render, which used to push the bottom of the
  // page below the viewport.
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{
        // Try to fill the height the host shell gives us. If the shell's
        // container doesn't define a height, height: 100% collapses to 0,
        // so we also set a minHeight based on the viewport minus an estimate
        // of the shell's chrome (~120px for topbar + a bit of breathing room).
        // Adjust SHELL_OFFSET if your shell is taller/shorter.
        height: '100%',
        minHeight: 'calc(100vh - 120px)',
        display: 'flex', flexDirection: 'column',
        bgcolor: 'background.default',
      }}>
        <Box sx={{ flexShrink: 0 }}>
          <Beacon4FilterBar
            exportButton={<Beacon4ExportButton containerRef={chartAreaRef} />}
            externalRole={externalRole}
          />
        </Box>

        {/* Body = remaining height. minHeight: 0 lets it shrink properly
            inside the flex column. */}
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden' }}>
          {/* Chart area. For Performance we hand the scroll over to the
              PerformanceView itself (it pins the table at the bottom and
              scrolls internally inside the DataGrid). For Blended we keep
              the previous outer-scroll behaviour. */}
          <Box
            ref={chartAreaRef}
            sx={{
              flex: 1, minWidth: 0, minHeight: 0,
              p: '12px 16px',
              overflowY: module === 'Performance' ? 'hidden' : 'auto',
              display: 'flex', flexDirection: 'column',
            }}
          >
            {module === 'Performance'
              ? <Beacon4PerformanceView externalRole={externalRole} />
              : <Beacon4BlendedView />}
          </Box>
          {module === 'Performance' ? <Beacon4PerformanceLegendPanel /> : <Beacon4LegendPanel />}
        </Box>
      </Box>
    </ThemeProvider>
  )
}